/**
 * MOCK FIXTURES — deterministic in-memory seed data for the webmail client.
 *
 * Everything in this file is fabricated. It mirrors the backend contract in
 * `src/api/types.ts` one-for-one so the entire UI (virtualized message list,
 * folder tree, calendar, contacts, sieve builder, notification settings) can be
 * developed and demoed with `VITE_USE_MOCKS=true` and no server at all.
 *
 * DELETE OR IGNORE THIS FILE ONCE THE GO BACKEND IS LIVE. Nothing here should
 * ever be imported by production code paths other than `src/api/mock.ts`.
 *
 * Determinism: no `Math.random()` anywhere. Ids are derived from stable index
 * arithmetic and all "random" text comes from a seeded mulberry32 PRNG keyed by
 * the entity id, so a given id always produces the same body/snippet within and
 * across runs. Dates are anchored to `new Date()` at module evaluation so the
 * calendar and message list always land in the currently visible ranges.
 */

import type {
  Account,
  AccountLimits,
  Address,
  AddressBook,
  AttachedMessage,
  Attachment,
  Attendee,
  Calendar,
  CalendarShareLink,
  CalendarEvent,
  CalendarInvite,
  Contact,
  Draft,
  Folder,
  MessageAuthentication,
  MessageDetail,
  MessageFlag,
  MessageSummary,
  NotificationPreferences,
  PushSubscriptionRecord,
  Session,
  SieveRule,
  SieveState,
} from '@/api';

/* ------------------------------------------------------------------ helpers */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** MOCK: evaluation-time anchor so fixtures always sit in the visible window. */
export const NOW: Date = new Date();

/** Deterministic 32-bit string hash (FNV-1a), used to seed the PRNG per entity. */
function hashString(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/** mulberry32 — tiny, stable, seedable PRNG. */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Index into a table with `noUncheckedIndexedAccess` satisfied. */
function pick<T>(table: readonly T[], index: number): T {
  if (table.length === 0) throw new Error('mock fixture table is empty');
  const value = table[((index % table.length) + table.length) % table.length];
  if (value === undefined) throw new Error('mock fixture table hole');
  return value;
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

function pad(value: number, width = 4): string {
  return String(value).padStart(width, '0');
}

/** Start of the month currently on screen. */
function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

function atTime(base: Date, dayOffset: number, hour: number, minute = 0): Date {
  return new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate() + dayOffset,
    hour,
    minute,
    0,
    0,
  );
}

/* ----------------------------------------------------------------- identity */

export const PRIMARY_ACCOUNT_ID = 'acct-primary';
export const SECONDARY_ACCOUNT_ID = 'acct-studio';
export const LEGACY_ACCOUNT_ID = 'acct-legacy';

export const PRIMARY_ADDRESS: Address = { name: 'Alex Rendel', email: 'alex@dao-mail.com' };
/** MOCK: the routing rule the primary address resolves to (see `Session.account`). */
export const PRIMARY_AUTH_HOST = 'mail01.cloud-hosting.io';
const SECONDARY_ADDRESS: Address = { name: 'Alex Rendel', email: 'alex@studio-nine.org' };
const LEGACY_ADDRESS: Address = { name: 'Alex Rendel', email: 'a.rendel@legacy-corp.net' };

/** MOCK: VAPID-shaped key. Not a real key; the mock push API never signs anything. */
export const MOCK_PUSH_PUBLIC_KEY =
  'BLc4xRzKlKORKWlbdgFaBrrPK3ydWAHo4M0gs0i1oEKgPpWC5cW4kx3YjKOBUsGZ0Cu1jMcYwC2xTYNlUw9d7pE';

/* ----------------------------------------------------------------- accounts */

/**
 * MOCK: the staging store's ceilings, as `GET /accounts` reports them (ADR
 * 0084 D7) -- the deployment's defaults, shared by every account.
 */
export const MOCK_ACCOUNT_LIMITS: AccountLimits = {
  maxAttachmentBytes: 24 * 1024 * 1024,
  maxAttachmentTotalBytes: 24 * 1024 * 1024,
  maxAttachments: 20,
};

function buildAccounts(): Account[] {
  return [
    {
      id: PRIMARY_ACCOUNT_ID,
      email: PRIMARY_ADDRESS.email,
      displayName: 'Alex Rendel',
      host: 'mail01.cloud-hosting.io',
      isPrimary: true,
      status: 'connected',
      color: '#4f7cff',
      // MiB, the wire's unit (plan/10): 16 GiB, 5.8 GiB used. These were
      // bytes, which the pane rendered as petabytes.
      quota: {
        storageLimit: 16 * 1024,
        storageUsage: 5927.1,
        countLimit: 250_000,
        countUsage: 41_882,
      },
      capabilities: {
        sieve: true,
        quota: true,
        passwordChange: true,
        calendar: true,
        contacts: true,
        push: true,
      },
      aliases: ['alex.rendel@dao-mail.com', 'billing@dao-mail.com'],
      limits: { ...MOCK_ACCOUNT_LIMITS },
    },
    {
      id: SECONDARY_ACCOUNT_ID,
      email: SECONDARY_ADDRESS.email,
      displayName: 'Studio Nine',
      host: 'mail04.cloud-hosting.io',
      isPrimary: false,
      status: 'connected',
      color: '#17a67c',
      // MOCK: deliberately near the ceiling so the near-quota warning is
      // reachable without waiting for a mailbox to actually fill up.
      quota: {
        storageLimit: 4 * 1024,
        storageUsage: 3840,
        countLimit: 50_000,
        countUsage: 47_310,
      },
      capabilities: {
        sieve: true,
        quota: true,
        passwordChange: false,
        calendar: true,
        contacts: true,
        push: false,
      },
      aliases: [],
      limits: { ...MOCK_ACCOUNT_LIMITS },
    },
    {
      id: LEGACY_ACCOUNT_ID,
      email: LEGACY_ADDRESS.email,
      displayName: 'Legacy Corp (IMAP)',
      host: 'imap.legacy-corp.net',
      isPrimary: false,
      // MOCK: deliberately broken so the sidebar's re-auth banner is reachable.
      status: 'reauth-required',
      statusMessage: 'The password for this mailbox was rejected. Sign in again to reconnect.',
      color: '#d9822b',
      // MOCK: no quota at all. A plain IMAP host has no Poste admin API behind
      // it, so its usage is genuinely unknowable (ADR 0008) -- which makes this
      // account the one that exercises the "unavailable" state.
      capabilities: {
        sieve: false,
        quota: false,
        passwordChange: false,
        calendar: false,
        contacts: true,
        push: false,
      },
      aliases: [],
      limits: { ...MOCK_ACCOUNT_LIMITS },
    },
  ];
}

/* ------------------------------------------------------------------ folders */

interface FolderSpec {
  slug: string;
  name: string;
  path: string;
  role: Folder['role'];
  parentSlug?: string;
  selectable?: boolean;
  subscribed?: boolean;
}

/**
 * MOCK: folder ids are `base64url(path)`, unpadded — the real encoding
 * (ADR 0012), so a client that tries to parse or rebuild one fails against the
 * mocks too instead of only against the backend.
 */
function base64url(raw: string): string {
  const bytes = new TextEncoder().encode(raw);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

const PRIMARY_FOLDER_SPECS: readonly FolderSpec[] = [
  { slug: 'inbox', name: 'Inbox', path: 'INBOX', role: 'inbox' },
  {
    slug: 'inbox-projects',
    name: 'Projects',
    path: 'INBOX/Projects',
    role: 'custom',
    parentSlug: 'inbox',
  },
  {
    slug: 'inbox-projects-acme',
    name: 'Acme',
    path: 'INBOX/Projects/Acme',
    role: 'custom',
    parentSlug: 'inbox-projects',
  },
  {
    slug: 'inbox-projects-northwind',
    name: 'Northwind',
    path: 'INBOX/Projects/Northwind',
    role: 'custom',
    parentSlug: 'inbox-projects',
  },
  {
    slug: 'inbox-newsletters',
    name: 'Newsletters',
    path: 'INBOX/Newsletters',
    role: 'custom',
    parentSlug: 'inbox',
  },
  { slug: 'drafts', name: 'Drafts', path: 'Drafts', role: 'drafts' },
  { slug: 'sent', name: 'Sent', path: 'Sent', role: 'sent' },
  { slug: 'archive', name: 'Archive', path: 'Archive', role: 'archive' },
  { slug: 'junk', name: 'Junk', path: 'Junk', role: 'junk' },
  { slug: 'trash', name: 'Trash', path: 'Trash', role: 'trash' },
  { slug: 'receipts', name: 'Receipts', path: 'Receipts', role: 'custom' },
  { slug: 'travel', name: 'Travel', path: 'Travel', role: 'custom' },
  // MOCK: a \Noselect container, so the tree renders a non-clickable node.
  {
    slug: 'travel-2024',
    name: '2024',
    path: 'Travel/2024',
    role: 'custom',
    parentSlug: 'travel',
    selectable: false,
  },
];

const SECONDARY_FOLDER_SPECS: readonly FolderSpec[] = [
  { slug: 'inbox', name: 'Inbox', path: 'INBOX', role: 'inbox' },
  {
    slug: 'inbox-clients',
    name: 'Clients',
    path: 'INBOX/Clients',
    role: 'custom',
    parentSlug: 'inbox',
  },
  { slug: 'drafts', name: 'Drafts', path: 'Drafts', role: 'drafts' },
  { slug: 'sent', name: 'Sent', path: 'Sent', role: 'sent' },
  { slug: 'archive', name: 'Archive', path: 'Archive', role: 'archive' },
  { slug: 'junk', name: 'Junk', path: 'Junk', role: 'junk' },
  { slug: 'trash', name: 'Trash', path: 'Trash', role: 'trash' },
  { slug: 'invoices', name: 'Invoices', path: 'Invoices', role: 'custom' },
];

const LEGACY_FOLDER_SPECS: readonly FolderSpec[] = [
  { slug: 'inbox', name: 'Inbox', path: 'INBOX', role: 'inbox' },
  { slug: 'drafts', name: 'Drafts', path: 'Drafts', role: 'drafts' },
  { slug: 'sent', name: 'Sent', path: 'Sent', role: 'sent' },
  { slug: 'trash', name: 'Trash', path: 'Trash', role: 'trash' },
];

/**
 * The opaque folder id for a mailbox in a given account.
 *
 * Keyed on the account code as well as the slug because the encoding's input is
 * the raw mailbox name, which repeats across accounts (`INBOX` everywhere)
 * while the id must not: the cache is account-scoped, but a test that reuses an
 * id across accounts should still be obviously wrong.
 */
export function folderId(accountCode: string, slug: string): string {
  return base64url(`${accountCode}:${FOLDER_PATHS[slug] ?? slug}`);
}

/** MOCK: slug → raw mailbox name, the string the id actually encodes. */
const FOLDER_PATHS: Record<string, string> = {
  inbox: 'INBOX',
  'inbox-projects': 'INBOX/Projects',
  'inbox-projects-acme': 'INBOX/Projects/Acme',
  'inbox-projects-northwind': 'INBOX/Projects/Northwind',
  'inbox-newsletters': 'INBOX/Newsletters',
  'inbox-clients': 'INBOX/Clients',
  drafts: 'Drafts',
  sent: 'Sent',
  archive: 'Archive',
  junk: 'Junk',
  trash: 'Trash',
  receipts: 'Receipts',
  travel: 'Travel',
  'travel-2024': 'Travel/2024',
  invoices: 'Invoices',
};

const ACCOUNT_CODES: Record<string, string> = {
  [PRIMARY_ACCOUNT_ID]: 'a1',
  [SECONDARY_ACCOUNT_ID]: 'a2',
  [LEGACY_ACCOUNT_ID]: 'a3',
};

function accountCode(accountId: string): string {
  return ACCOUNT_CODES[accountId] ?? 'ax';
}

function buildFolders(accountId: string, specs: readonly FolderSpec[]): Folder[] {
  const code = accountCode(accountId);
  return specs.map((spec) => ({
    id: folderId(code, spec.slug),
    accountId,
    path: spec.path,
    name: spec.name,
    parentId: spec.parentSlug === undefined ? null : folderId(code, spec.parentSlug),
    role: spec.role,
    // A custom folder has no provenance (the server omits role_source);
    // 'user' is reserved for a role the person chose in Settings (#68).
    ...(spec.role === 'custom' ? {} : { roleSource: 'special-use' as const }),
    delimiter: '/',
    unreadCount: 0,
    totalCount: 0,
    selectable: spec.selectable ?? true,
    subscribed: spec.subscribed ?? true,
    uidValidity: 1_694_000_000 + hashString(`${accountId}:${spec.slug}`) % 100_000,
  }));
}

/* ----------------------------------------------------------------- messages */

interface MessageCombo {
  from: Address;
  subject: string;
  snippet: string;
}

/** MOCK: ~25 plausible sender/subject/snippet combos, recombined into hundreds of rows. */
const MESSAGE_COMBOS: readonly MessageCombo[] = [
  {
    from: { name: 'Priya Raman', email: 'priya.raman@acme-industrial.com' },
    subject: 'Acme migration — cutover window confirmed',
    snippet:
      'We locked the maintenance window for Saturday 02:00–06:00 UTC. Ops will freeze writes an hour before.',
  },
  {
    from: { name: 'Tomas Lindqvist', email: 'tomas@northwind-labs.se' },
    subject: 'Re: Northwind API rate limits',
    snippet:
      'Bumping you to 2,000 req/min on the staging tier for now. Let me know if the burst behaviour still bites.',
  },
  {
    from: { name: 'GitLab', email: 'no-reply@gitlab-mirror.io' },
    subject: 'Pipeline failed on main (#41892)',
    snippet: 'Job "integration:imap" exited with code 1 after 4m12s. Last green run was 3 commits ago.',
  },
  {
    from: { name: 'Hanna Osei', email: 'hanna.osei@ledgerworks.co' },
    subject: 'Invoice LW-2291 is ready',
    snippet: 'Attached is the invoice for the September retainer. Net 30, payable to the usual account.',
  },
  {
    from: { name: 'Dmitri Volkov', email: 'd.volkov@cloud-hosting.io' },
    subject: 'Scheduled maintenance on mail01',
    snippet:
      'We will move mail01 to new NVMe storage next Thursday. Expect up to 15 minutes of IMAP interruption.',
  },
  {
    from: { name: 'Sofia Marchetti', email: 'sofia@studio-nine.org' },
    subject: 'Brand refresh — second pass',
    snippet:
      'Second pass on the wordmark is in the shared drive. I kept the tighter kerning you preferred on variant C.',
  },
  {
    from: { name: 'Calendar', email: 'calendar-notification@dao-mail.com' },
    subject: 'Invitation: Quarterly roadmap review',
    snippet: 'You have been invited to Quarterly roadmap review. Please respond with your availability.',
  },
  {
    from: { name: 'Jules Vandermeer', email: 'jules@vandermeer-legal.be' },
    subject: 'Contract redline — clause 7.3',
    snippet:
      'Their counsel pushed back on the indemnity cap. I suggest we hold at 12 months of fees and move on.',
  },
  {
    from: { name: 'The Terminal Weekly', email: 'digest@terminal-weekly.dev' },
    subject: 'Issue #214: the quiet return of local-first software',
    snippet: 'Also inside: CRDT war stories, a very small SQLite wrapper, and why your CI is slow.',
  },
  {
    from: { name: 'Nadia Khoury', email: 'nadia.khoury@meridian-health.org' },
    subject: 'Onboarding docs for the new intake flow',
    snippet:
      'I trimmed the walkthrough to four screens. Anything longer and the front desk stops reading it.',
  },
  {
    from: { name: 'Ops Alerts', email: 'alerts@mail01.cloud-hosting.io' },
    subject: '[WARN] Dovecot connection pool at 82%',
    snippet: 'Threshold 80% crossed for 5 consecutive minutes on mail01. Auto-scaling is not enabled.',
  },
  {
    from: { name: 'Ben Okafor', email: 'ben@okafor-design.studio' },
    subject: 'Fwd: Print proofs arrived',
    snippet: 'Proofs look good except the cover stock — it is noticeably warmer than the swatch.',
  },
  {
    from: { name: 'Elena Fischer', email: 'e.fischer@bundesarchiv-demo.de' },
    subject: 'Archive export — batch 3 of 7',
    snippet: 'Batch 3 finished overnight, 44,318 records. Checksums match the manifest you sent.',
  },
  {
    from: { name: 'Stripe', email: 'receipts@billing-demo.stripe-mirror.com' },
    subject: 'Your receipt from Cloud Hosting IO',
    snippet: 'Thanks for your payment of €89.00. This receipt covers the period 01–30 September.',
  },
  {
    from: { name: 'Mateo Rivas', email: 'mateo.rivas@puertoverde.mx' },
    subject: 'Re: Re: shipping window slipped again',
    snippet:
      'The carrier moved us to the 14th. I pushed for the 9th but the port backlog is genuinely bad this month.',
  },
  {
    from: { name: 'Yuki Tanaka', email: 'yuki@tanaka-consulting.jp' },
    subject: 'Notes from the Tuesday sync',
    snippet: 'Three decisions, two open questions, one thing nobody wants to own. Notes attached.',
  },
  {
    from: { name: 'Security', email: 'security@dao-mail.com' },
    subject: 'New sign-in from Firefox on Linux',
    snippet:
      'We noticed a sign-in from a device we do not recognise. If this was you, no action is needed.',
  },
  {
    from: { name: 'Clara Nowak', email: 'clara.nowak@helios-energy.pl' },
    subject: 'Grid data feed keeps dropping at 03:00',
    snippet:
      'Every night at almost exactly 03:00 the feed stalls for ~90 seconds. Smells like a cron collision.',
  },
  {
    from: { name: 'Recruiting', email: 'talent@harborline-search.com' },
    subject: 'Quick question about your backend role',
    snippet: 'I work with a few infrastructure engineers who have done exactly this kind of migration.',
  },
  {
    from: { name: 'Ingrid Haugen', email: 'ingrid@fjordkraft-demo.no' },
    subject: 'Site visit: Bergen, week 42',
    snippet: 'We can host Tuesday or Wednesday. Bring boots — the substation yard is not forgiving.',
  },
  {
    from: { name: 'Docs Bot', email: 'docs-bot@internal-tools.dev' },
    subject: 'Weekly documentation drift report',
    snippet: '12 endpoints changed, 4 docs updated. The gap is mostly in the calendar surface.',
  },
  {
    from: { name: 'Rafael Costa', email: 'rafael.costa@atlantico.pt' },
    subject: 'Translation review — PT-PT strings',
    snippet:
      'I corrected 31 strings. Several were machine-translated from the Brazilian file and read oddly.',
  },
  {
    from: { name: 'Maya Sundström', email: 'maya@quietfield.io' },
    subject: 'Coffee next week?',
    snippet: 'I am in town Thursday and Friday. Would love to hear how the self-hosting experiment went.',
  },
  {
    from: { name: 'Finance', email: 'finance@studio-nine.org' },
    subject: 'Expense report needs one more line item',
    snippet: 'The October report is missing the conference ticket. Add it and I will push it through.',
  },
  {
    from: { name: 'Postmaster', email: 'postmaster@imap.legacy-corp.net' },
    subject: 'Mailbox quota warning (94%)',
    snippet: 'Your mailbox is at 94% of its 2 GB quota. Messages may be rejected once it is full.',
  },
];

const ATTACHMENT_TABLE: readonly { filename: string; contentType: string; sizeBytes: number }[] = [
  { filename: 'cutover-runbook.pdf', contentType: 'application/pdf', sizeBytes: 482_113 },
  { filename: 'invoice-LW-2291.pdf', contentType: 'application/pdf', sizeBytes: 91_204 },
  { filename: 'roadmap-q4.xlsx', contentType: 'application/vnd.ms-excel', sizeBytes: 218_990 },
  { filename: 'wordmark-variant-c.png', contentType: 'image/png', sizeBytes: 1_204_338 },
  { filename: 'sync-notes.md', contentType: 'text/markdown', sizeBytes: 4_812 },
  { filename: 'manifest-batch-3.csv', contentType: 'text/csv', sizeBytes: 66_402 },
  { filename: 'proofs.zip', contentType: 'application/zip', sizeBytes: 8_441_920 },
  // A forwarded email (ADR 0092): opened in the app by `attached` below.
  { filename: 'Fwd - Quarterly numbers.eml', contentType: 'message/rfc822', sizeBytes: 38_120 },
];

interface MessageSpec {
  accountId: string;
  folderSlug: string;
  count: number;
  /** How far back the oldest message in this folder should sit, in days. */
  spanDays: number;
  /** Outgoing folders (Sent/Drafts) put the account owner in `from`. */
  owner?: Address;
  outgoing?: boolean;
  draft?: boolean;
  allRead?: boolean;
}

function buildMessages(spec: MessageSpec): MessageSummary[] {
  const code = accountCode(spec.accountId);
  const fid = folderId(code, spec.folderSlug);
  const rnd = mulberry32(hashString(`${fid}:messages`));
  const step = (spec.spanDays * DAY) / Math.max(spec.count, 1);
  const base = NOW.getTime();
  const out: MessageSummary[] = [];

  for (let i = 0; i < spec.count; i += 1) {
    const id = `msg-${code}-${spec.folderSlug}-${pad(i + 1)}`;
    const combo = pick(MESSAGE_COMBOS, i * 7 + Math.floor(rnd() * 5));
    const jitter = Math.floor(rnd() * (step * 0.6));
    const when = base - 2 * HOUR - i * step - jitter;

    const owner = spec.owner ?? PRIMARY_ADDRESS;
    const outgoing = spec.outgoing === true;
    const from = outgoing ? owner : combo.from;
    const to: Address[] = outgoing ? [combo.from] : [owner];

    const flags: MessageFlag[] = [];
    const unread = !spec.allRead && !outgoing && (i < 9 || i % 13 === 0);
    if (!unread) flags.push('seen');
    if (i % 17 === 0) flags.push('flagged');
    if (i % 9 === 4) flags.push('answered');
    if (i % 23 === 6) flags.push('forwarded');
    if (spec.draft === true) flags.push('draft');

    const hasAttachments = i % 7 === 0 || i % 11 === 3;
    const threadSize = i % 11 === 0 ? 2 + (i % 4) : 1;
    const subjectPrefix = threadSize > 1 && i % 2 === 0 ? 'Re: ' : '';

    out.push({
      id,
      accountId: spec.accountId,
      folderId: fid,
      threadId: `thr-${code}-${pad(Math.floor(i / 3) + 1)}`,
      subject: `${subjectPrefix}${combo.subject}`,
      from,
      to,
      date: iso(when),
      snippet: combo.snippet,
      flags,
      hasAttachments,
      ...(threadSize > 1 ? { threadCount: threadSize } : {}),
      sizeBytes: 3_200 + Math.floor(rnd() * 180_000),
    });
  }

  // Newest first — the list view's default sort.
  out.sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  return out;
}

/** MOCK: message ids that carry an iTIP invite, so the invite card is reachable. */
export const INVITE_MESSAGE_IDS: readonly string[] = [
  'msg-a1-inbox-0004',
  'msg-a1-inbox-0031',
  'msg-a1-inbox-0102',
  // An unverified REQUEST: the one invitation whose reply has to be confirmed.
  'msg-a1-inbox-0006',
];

/**
 * MOCK: a message whose HTML the server would not show safely (#42), so the
 * reading pane's render-status notice is reachable: it carries text only.
 */
export const TEXT_ONLY_MESSAGE_ID = 'msg-a1-inbox-0009';

/* -------------------------------------------------------- message bodies */

const BODY_OPENERS: readonly string[] = [
  'Thanks for the quick turnaround on this.',
  'Following up on the thread from earlier this week.',
  'Short one, but I wanted it in writing rather than in chat.',
  'Apologies for the delay — this got buried under the migration work.',
  'Circling back now that the numbers have settled.',
];

const BODY_MIDDLES: readonly string[] = [
  'The short version is that the current approach holds up under normal load but degrades badly the moment we exceed roughly four hundred concurrent sessions. We have two options: raise the pool ceiling and accept the memory cost, or shard the connections per mailbox host.',
  'I walked through the timeline with the team and the only genuinely immovable date is the audit on the 28th. Everything before that has at least a few days of slack, which gives us room to stage the rollout rather than doing it all at once.',
  'One thing worth flagging: the export format changed underneath us, so anything we generated before the 3rd will need to be reprocessed. It is not a large job, but it is not a zero-minute job either.',
  'Budget-wise we are still inside the envelope, though the hosting line is running about eleven percent hot. That is entirely the extra storage, and it will flatten once the archive batches finish.',
  'I would rather we decide this deliberately than let it get decided by whoever touches the code next. Happy to take either path, but let us pick one before Thursday.',
];

const BODY_CLOSERS: readonly string[] = [
  'Let me know if that works and I will get it on the calendar.',
  'Shout if any of this looks wrong — I am going from memory on a couple of the figures.',
  'No rush on a reply; end of week is fine.',
  'Happy to jump on a call if that is faster than email.',
  'Thanks again for pushing this along.',
];

function signatureFor(address: Address): string {
  const name = address.name ?? address.email;
  const domain = address.email.split('@')[1] ?? 'example.com';
  return `${name}\n${domain}\n--\nSent with OLS Mail`;
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** MOCK: deterministic body generated on demand from the summary. */
export function buildBody(summary: MessageSummary): { html: string; text: string } {
  const rnd = mulberry32(hashString(summary.id));
  const paragraphs: string[] = [
    pick(BODY_OPENERS, Math.floor(rnd() * BODY_OPENERS.length)),
    pick(BODY_MIDDLES, Math.floor(rnd() * BODY_MIDDLES.length)),
    pick(BODY_MIDDLES, Math.floor(rnd() * BODY_MIDDLES.length) + 2),
    pick(BODY_CLOSERS, Math.floor(rnd() * BODY_CLOSERS.length)),
  ];
  const signature = signatureFor(summary.from);
  const text = `${paragraphs.join('\n\n')}\n\n--\n${signature}\n`;
  const html = [
    '<div class="mock-body">',
    ...paragraphs.map((p) => `  <p>${escapeHtml(p)}</p>`),
    `  <p class="mock-signature">${escapeHtml(signature).replace(/\n/g, '<br>')}</p>`,
    '</div>',
  ].join('\n');
  return { html, text };
}

function buildAttachments(summary: MessageSummary): Attachment[] {
  if (!summary.hasAttachments) return [];
  const rnd = mulberry32(hashString(`${summary.id}:att`));
  const count = 1 + Math.floor(rnd() * 2);
  const out: Attachment[] = [];
  for (let i = 0; i < count; i += 1) {
    const entry = pick(ATTACHMENT_TABLE, hashString(summary.id) + i);
    const id = `att-${summary.id}-${i + 1}`;
    out.push({
      id,
      filename: entry.filename,
      contentType: entry.contentType,
      sizeBytes: entry.sizeBytes,
      inline: false,
      downloadUrl: `/api/v1/messages/${summary.id}/attachments/${id}`,
      ...(entry.contentType === 'message/rfc822' ? { attachedMessage: true, attachedPath: String(i + 2) } : {}),
    });
  }
  return out;
}

/**
 * MOCK: an email attached to a message (ADR 0092). `path` is part ids joined
 * by `-`: the first level carries a PDF and a second forwarded email, the
 * second level is the original.
 */
export function buildAttachedMessage(accountId: string, messageId: string, path: string): AttachedMessage {
  const depth = path.split('-').length;
  const base = `/api/v1/accounts/${accountId}/messages/${messageId}/attached/${path}`;
  const deep = depth >= 2;
  return {
    path,
    renderStatus: 'ok',
    filename: deep ? 'The original.eml' : 'Fwd - Quarterly numbers.eml',
    downloadUrl: `/api/v1/accounts/${accountId}/messages/${messageId}/parts/${path.split('-')[0]}`,
    sizeBytes: deep ? 2_104 : 38_120,
    subject: deep ? 'The original' : 'Quarterly numbers – final',
    from: [deep ? { name: 'Origin', email: 'origin@deep.example' } : { name: 'Renée Müller', email: 'renee@contoso.example' }],
    to: [{ name: 'Alex Owner', email: 'alex@example.org' }],
    cc: deep ? [] : [{ name: 'Team', email: 'team@contoso.example' }],
    replyTo: [],
    date: '2026-09-21T14:02:11Z',
    rfcMessageId: deep ? 'original@deep.example' : 'DB9PR0101MB1234@outlook.example',
    bodyHtml: deep
      ? '<p>The <i>original</i> message.</p>'
      : '<div><p>Here are the numbers – see the chart and the report attached.</p><p>Renée</p></div>',
    bodyText: deep ? 'The original message.' : 'Here are the numbers – see the chart and the report attached.\n\nRenée',
    renderVersion: 4,
    sanitizeReport: { remoteImagesBlocked: 0, scriptsRemoved: 0, formsRemoved: 0 },
    remoteContent: { mode: 'blocked', blocked: 0, loaded: 0, trackersRemoved: 0 },
    attachments: deep
      ? []
      : [
          {
            id: '2',
            filename: 'Q3-report.pdf',
            contentType: 'application/pdf',
            sizeBytes: 91_204,
            inline: false,
            downloadUrl: `${base}/parts/2`,
          },
          {
            id: '3',
            filename: 'The original.eml',
            contentType: 'message/rfc822',
            sizeBytes: 2_104,
            inline: false,
            downloadUrl: `${base}/parts/3`,
            attachedMessage: true,
            attachedPath: `${path}-3`,
          },
        ],
    headers: [
      { name: 'Subject', value: deep ? 'The original' : 'Quarterly numbers – final' },
      { name: 'Message-ID', value: deep ? '<original@deep.example>' : '<DB9PR0101MB1234@outlook.example>' },
    ],
  };
}

function buildInvite(summary: MessageSummary): CalendarInvite | undefined {
  const slot = INVITE_MESSAGE_IDS.indexOf(summary.id);
  if (slot === -1) return undefined;
  const start = atTime(NOW, slot + 2, 10 + slot, 0);
  const end = new Date(start.getTime() + (slot === 1 ? 90 : 60) * MINUTE);
  const titles = [
    'Quarterly roadmap review',
    'Acme cutover dry run',
    'Design critique: intake flow',
    'Vendor security questionnaire',
  ];
  const locations = [
    'Room Arvo / Jitsi',
    'https://meet.dao-mail.com/acme-cutover',
    'Studio Nine, floor 3',
    'Phone',
  ];
  const attendees: Attendee[] = [
    { ...summary.from, status: 'accepted' },
    { ...PRIMARY_ADDRESS, status: 'needs-action' },
    { name: 'Yuki Tanaka', email: 'yuki@tanaka-consulting.jp', status: 'tentative' },
    { name: 'Ben Okafor', email: 'ben@okafor-design.studio', status: 'needs-action', optional: true },
  ];
  // Methods, not just REQUEST: the card renders three of them differently and
  // only one of them takes a reply, so the fixtures have to be able to show it.
  const method = pick(['REQUEST', 'CANCEL', 'REPLY'] as const, slot);
  return {
    uid: `invite-${summary.id}@dao-mail.com`,
    method,
    partId: '2',
    event: {
      // The ICS is not on a calendar yet, which is exactly why `calendar_id`
      // is empty and the reply has to name one.
      id: `invite-${summary.id}`,
      calendarId: '',
      uid: `invite-${summary.id}@dao-mail.com`,
      title: pick(titles, slot),
      start: start.toISOString(),
      end: end.toISOString(),
      allDay: false,
      status: method === 'CANCEL' ? 'cancelled' : 'confirmed',
      location: pick(locations, slot),
      // The first invitation carries a CONFERENCE property, projected by the
      // server as `video_call_url`, so the card's Join button has a home.
      ...(slot === 0 ? { videoCallUrl: 'https://meet.jit.si/ols-roadmap-7qk2m4xw9rtd3vbn' } : {}),
      organizer: summary.from,
      attendees,
      reminders: [],
    },
    myStatus: 'needs-action',
    // Two unverified fixtures, so the warning has a home: a CANCEL (slot 1)
    // and a REQUEST (slot 3), which is the one whose reply must be confirmed.
    trusted: slot !== 1 && slot !== 3,
    sequence: slot,
  };
}

/** MOCK: expand a list row into the full detail the reading pane wants. */
export function buildMessageDetail(summary: MessageSummary): MessageDetail {
  const { html, text } = buildBody(summary);
  const invite = buildInvite(summary);
  const rnd = mulberry32(hashString(`${summary.id}:cc`));
  const cc: Address[] =
    rnd() > 0.72 ? [{ name: 'Sofia Marchetti', email: 'sofia@studio-nine.org' }] : [];
  const textOnly = summary.id === TEXT_ONLY_MESSAGE_ID;

  return {
    ...summary,
    cc,
    bcc: [],
    replyTo: [summary.from],
    // A refused body reaches the client as text alone, as the server sends it.
    ...(textOnly ? {} : { bodyHtml: html }),
    bodyText: text,
    attachments: buildAttachments(summary),
    // Ordered pairs, duplicates preserved — `Received` is the reason the wire
    // shape is an array and not a map (ADR 0009).
    headers: [
      // MOCK: the two stamps the headers view (#324) exists to tell apart —
      // ours, and one the sender wrote for themselves. The panel reads the
      // first and counts the second as ignored.
      {
        name: 'Authentication-Results',
        value: authResultsFor(summary),
      },
      {
        name: 'Authentication-Results',
        value: `${domainOfAddress(summary.from.email)}; dkim=pass header.d=${domainOfAddress(summary.from.email)}; spf=pass; dmarc=pass`,
      },
      {
        name: 'Received',
        value:
          `from mx2.example.net (mx2.example.net [203.0.113.42]) by mail.example.com ` +
          `(Postfix) with ESMTPS id 4A2B; ${new Date(summary.date).toUTCString()}`,
      },
      { name: 'Received', value: `from mx1.example.net; ${new Date(summary.date).toUTCString()}` },
      { name: 'Return-Path', value: `<bounces@${domainOfAddress(summary.from.email)}>` },
      { name: 'Message-Id', value: `<${summary.id}@dao-mail.com>` },
      { name: 'From', value: formatAddress(summary.from) },
      { name: 'To', value: summary.to.map(formatAddress).join(', ') },
      { name: 'Subject', value: summary.subject },
      { name: 'Date', value: new Date(summary.date).toUTCString() },
      { name: 'MIME-Version', value: '1.0' },
      { name: 'Content-Type', value: 'multipart/alternative; boundary="mock-boundary"' },
      { name: 'X-Mailer', value: 'OLS Mock Fixtures' },
      { name: 'X-Spam-Score', value: (Math.round(rnd() * 400) / 100).toFixed(2) },
    ],
    sanitizeReport: { remoteImagesBlocked: 0, scriptsRemoved: 0, formsRemoved: 0 },
    ...(invite !== undefined ? { invite } : {}),
    ...unsubscribeFor(summary),
    renderStatus: textOnly ? 'text_only' : 'ok',
  };
}

/**
 * MOCK: a List-Unsubscribe route for some of the mail, one of each method, so
 * all three affordances have a home.
 */
function unsubscribeFor(summary: MessageSummary): Pick<MessageDetail, 'unsubscribe'> {
  const domain = domainOfAddress(summary.from.email);
  if (domain === '') return {};
  const roll = hashString(`${summary.id}:unsubscribe`) % 6;
  if (roll === 0) {
    return {
      unsubscribe: {
        method: 'one_click',
        target: `https://lists.${domain}/unsubscribe/one-click?m=${encodeURIComponent(summary.id)}`,
      },
    };
  }
  if (roll === 1) {
    return {
      unsubscribe: {
        method: 'mailto',
        target: `unsubscribe@${domain}`,
        subject: 'unsubscribe',
        body: `Please remove ${summary.to[0]?.email ?? 'me'} from this list.`,
      },
    };
  }
  if (roll === 2) {
    return {
      unsubscribe: { method: 'link', target: `https://${domain}/email-preferences` },
    };
  }
  return {};
}

/** MOCK: the domain half of an address. */
function domainOfAddress(email: string): string {
  const at = email.lastIndexOf('@');
  return at < 0 ? '' : email.slice(at + 1).toLowerCase();
}

/**
 * MOCK: the Authentication-Results our own host would have stamped.
 *
 * Not every fixture passes: one message in four records a DKIM failure and
 * one in five records nothing at all, so the panel's "your mail server did
 * not record a check" state has a home in the mock the way the unverified
 * invite does.
 */
function authResultsFor(summary: MessageSummary): string {
  const domain = domainOfAddress(summary.from.email);
  const roll = hashString(`${summary.id}:auth`) % 5;
  if (roll === 0) return '';
  const dkim = roll === 1 ? 'fail' : 'pass';
  const dmarc = roll === 1 ? 'fail' : 'pass';
  return (
    `mail.example.com; spf=pass smtp.mailfrom=bounces@${domain}; ` +
    `dkim=${dkim} header.d=${domain}; dmarc=${dmarc} header.from=${domain}`
  );
}

/** MOCK: the authentication summary the backend would compute (#324). */
export function buildMessageAuthentication(summary: MessageSummary): MessageAuthentication {
  const domain = domainOfAddress(summary.from.email);
  const raw = authResultsFor(summary);
  const trusted = raw !== '';
  const dkim = raw.includes('dkim=fail') ? 'fail' : 'pass';
  return {
    authservId: 'mail.example.com',
    trusted,
    ...(trusted ? { raw } : {}),
    ignoredHeaders: 1,
    spf: trusted ? { result: 'pass', domain: `bounces@${domain}` } : { result: '' },
    dkim: trusted ? { result: dkim, domain } : { result: '' },
    dmarc: trusted ? { result: dkim } : { result: '' },
    fromAddress: summary.from.email,
    fromDomain: domain,
    spfCheck: {
      checked: true,
      outcome: 'pass',
      domain,
      record: `v=spf1 ip4:203.0.113.0/24 include:_spf.${domain} -all`,
      mechanism: 'ip4:203.0.113.0/24',
      clientIp: '203.0.113.42',
      lookups: 2,
      envelopeFrom: `bounces@${domain}`,
    },
    // One fixture in three carries a Reply-To on another domain, so the
    // "differences worth a look" list is not permanently empty in the mock.
    observations:
      hashString(`${summary.id}:obs`) % 3 === 0
        ? [{ kind: 'reply_to_domain', from: domain, found: `reply.${domain}` }]
        : [],
  };
}

export function formatAddress(address: Address): string {
  return address.name === undefined ? address.email : `${address.name} <${address.email}>`;
}

/** MOCK: a plausible RFC822 rendering for the "view source" pane. */
export function buildRawSource(detail: MessageDetail): string {
  const headers = detail.headers.map((header) => `${header.name}: ${header.value}`).join('\n');
  return [
    headers,
    '',
    '--mock-boundary',
    'Content-Type: text/plain; charset=utf-8',
    '',
    detail.bodyText ?? '',
    '--mock-boundary',
    'Content-Type: text/html; charset=utf-8',
    '',
    detail.bodyHtml ?? '',
    '--mock-boundary--',
    '',
  ].join('\n');
}

/* ------------------------------------------------------------------ drafts */

function buildDrafts(): Draft[] {
  return [
    {
      id: 'draft-a1-0001',
      accountId: PRIMARY_ACCOUNT_ID,
      fromAddress: PRIMARY_ADDRESS.email,
      to: [{ name: 'Priya Raman', email: 'priya.raman@acme-industrial.com' }],
      cc: [],
      bcc: [],
      subject: 'Re: Acme migration — cutover window confirmed',
      bodyHtml: '<p>Confirming 02:00 UTC works on our side. I will be on the bridge from 01:45.</p>',
      bodyText: 'Confirming 02:00 UTC works on our side. I will be on the bridge from 01:45.',
      attachmentIds: [],
      inReplyToMessageId: 'msg-a1-inbox-0001',
      mode: 'reply',
      updatedAt: iso(NOW.getTime() - 3 * HOUR),
    },
    {
      id: 'draft-a1-0002',
      accountId: PRIMARY_ACCOUNT_ID,
      fromAddress: PRIMARY_ADDRESS.email,
      to: [],
      cc: [],
      bcc: [],
      subject: 'Quarterly hosting review — agenda',
      bodyHtml: '<p>Draft agenda:</p><ol><li>Storage growth</li><li>Sieve rollout</li></ol>',
      bodyText: 'Draft agenda:\n1. Storage growth\n2. Sieve rollout',
      attachmentIds: [],
      mode: 'new',
      updatedAt: iso(NOW.getTime() - 2 * DAY),
    },
    {
      id: 'draft-a2-0001',
      accountId: SECONDARY_ACCOUNT_ID,
      fromAddress: SECONDARY_ADDRESS.email,
      to: [{ name: 'Ben Okafor', email: 'ben@okafor-design.studio' }],
      cc: [],
      bcc: [],
      subject: 'Print proofs — cover stock',
      bodyHtml: '<p>The cover stock reads warmer than the swatch. Can we see the alternative?</p>',
      attachmentIds: [],
      mode: 'new',
      updatedAt: iso(NOW.getTime() - 5 * HOUR),
    },
  ];
}

/* ---------------------------------------------------------------- contacts */

function buildAddressBooks(): AddressBook[] {
  return [
    {
      id: 'abook-a1-personal',
      accountId: PRIMARY_ACCOUNT_ID,
      name: 'Personal',
      readOnly: false,
      href: '/dav/addressbooks/alex@dao-mail.com/personal/',
      contactCount: 0,
    },
    {
      id: 'abook-a1-directory',
      accountId: PRIMARY_ACCOUNT_ID,
      name: 'Company directory',
      readOnly: true,
      href: '/dav/addressbooks/alex@dao-mail.com/directory/',
      contactCount: 0,
    },
    {
      id: 'abook-a2-clients',
      accountId: SECONDARY_ACCOUNT_ID,
      name: 'Clients',
      readOnly: false,
      href: '/dav/addressbooks/alex@studio-nine.org/clients/',
      contactCount: 0,
    },
  ];
}

const CONTACT_TABLE: readonly {
  first: string;
  last: string;
  email: string;
  org: string;
  title: string;
  city: string;
  country: string;
  phone: string;
}[] = [
  { first: 'Priya', last: 'Raman', email: 'priya.raman@acme-industrial.com', org: 'Acme Industrial', title: 'Head of Platform', city: 'Pune', country: 'India', phone: '+91 20 5550 1188' },
  { first: 'Tomas', last: 'Lindqvist', email: 'tomas@northwind-labs.se', org: 'Northwind Labs', title: 'Staff Engineer', city: 'Malmö', country: 'Sweden', phone: '+46 40 555 0142' },
  { first: 'Hanna', last: 'Osei', email: 'hanna.osei@ledgerworks.co', org: 'Ledgerworks', title: 'Controller', city: 'Accra', country: 'Ghana', phone: '+233 30 255 0119' },
  { first: 'Dmitri', last: 'Volkov', email: 'd.volkov@cloud-hosting.io', org: 'Cloud Hosting IO', title: 'SRE Lead', city: 'Tallinn', country: 'Estonia', phone: '+372 555 0177' },
  { first: 'Sofia', last: 'Marchetti', email: 'sofia@studio-nine.org', org: 'Studio Nine', title: 'Creative Director', city: 'Bologna', country: 'Italy', phone: '+39 051 555 0123' },
  { first: 'Jules', last: 'Vandermeer', email: 'jules@vandermeer-legal.be', org: 'Vandermeer Legal', title: 'Partner', city: 'Ghent', country: 'Belgium', phone: '+32 9 555 0164' },
  { first: 'Nadia', last: 'Khoury', email: 'nadia.khoury@meridian-health.org', org: 'Meridian Health', title: 'Programme Manager', city: 'Beirut', country: 'Lebanon', phone: '+961 1 555 019' },
  { first: 'Ben', last: 'Okafor', email: 'ben@okafor-design.studio', org: 'Okafor Design', title: 'Principal', city: 'Lagos', country: 'Nigeria', phone: '+234 1 555 0151' },
  { first: 'Elena', last: 'Fischer', email: 'e.fischer@bundesarchiv-demo.de', org: 'Archive Services', title: 'Archivist', city: 'Koblenz', country: 'Germany', phone: '+49 261 555 0198' },
  { first: 'Mateo', last: 'Rivas', email: 'mateo.rivas@puertoverde.mx', org: 'Puerto Verde', title: 'Logistics', city: 'Veracruz', country: 'Mexico', phone: '+52 229 555 0134' },
  { first: 'Yuki', last: 'Tanaka', email: 'yuki@tanaka-consulting.jp', org: 'Tanaka Consulting', title: 'Consultant', city: 'Kyoto', country: 'Japan', phone: '+81 75 555 0176' },
  { first: 'Clara', last: 'Nowak', email: 'clara.nowak@helios-energy.pl', org: 'Helios Energy', title: 'Data Engineer', city: 'Kraków', country: 'Poland', phone: '+48 12 555 0143' },
  { first: 'Ingrid', last: 'Haugen', email: 'ingrid@fjordkraft-demo.no', org: 'Fjordkraft', title: 'Site Manager', city: 'Bergen', country: 'Norway', phone: '+47 55 55 0128' },
  { first: 'Rafael', last: 'Costa', email: 'rafael.costa@atlantico.pt', org: 'Atlântico', title: 'Localisation', city: 'Porto', country: 'Portugal', phone: '+351 22 555 0117' },
  { first: 'Maya', last: 'Sundström', email: 'maya@quietfield.io', org: 'Quietfield', title: 'Founder', city: 'Gothenburg', country: 'Sweden', phone: '+46 31 555 0109' },
  { first: 'Omar', last: 'Haddad', email: 'omar.haddad@sandline.ae', org: 'Sandline', title: 'Account Director', city: 'Dubai', country: 'UAE', phone: '+971 4 555 0192' },
  { first: 'Lucia', last: 'Ferreira', email: 'lucia@ferreira-arq.br', org: 'Ferreira Arquitetura', title: 'Architect', city: 'Recife', country: 'Brazil', phone: '+55 81 5555 0188' },
  { first: 'Anders', last: 'Kjeldsen', email: 'anders@nordbyte.dk', org: 'Nordbyte', title: 'CTO', city: 'Aarhus', country: 'Denmark', phone: '+45 55 55 0166' },
  { first: 'Grace', last: 'Mwangi', email: 'grace.mwangi@savannahnet.ke', org: 'Savannah Net', title: 'Network Architect', city: 'Nairobi', country: 'Kenya', phone: '+254 20 555 0121' },
  { first: 'Hugo', last: 'Bernard', email: 'hugo.bernard@caillou.fr', org: 'Caillou', title: 'Product Manager', city: 'Lyon', country: 'France', phone: '+33 4 55 55 0173' },
  { first: 'Sana', last: 'Iqbal', email: 'sana.iqbal@zenithsoft.pk', org: 'Zenith Soft', title: 'QA Lead', city: 'Lahore', country: 'Pakistan', phone: '+92 42 555 0137' },
  { first: 'Viktor', last: 'Novak', email: 'viktor.novak@modra.cz', org: 'Modrá', title: 'DevOps', city: 'Brno', country: 'Czechia', phone: '+420 5 5555 0155' },
  { first: 'Aiko', last: 'Mori', email: 'aiko.mori@hinode-labs.jp', org: 'Hinode Labs', title: 'Researcher', city: 'Sapporo', country: 'Japan', phone: '+81 11 555 0148' },
  { first: 'Daniel', last: 'Whitfield', email: 'daniel@whitfield-audit.co.uk', org: 'Whitfield Audit', title: 'Auditor', city: 'Leeds', country: 'United Kingdom', phone: '+44 113 555 0182' },
  { first: 'Zara', last: 'Ahmadi', email: 'zara@lumenpath.ca', org: 'Lumenpath', title: 'Designer', city: 'Montréal', country: 'Canada', phone: '+1 514 555 0161' },
  { first: 'Pablo', last: 'Guerrero', email: 'pablo.guerrero@altamar.es', org: 'Altamar', title: 'Sales', city: 'Valencia', country: 'Spain', phone: '+34 96 555 0113' },
  { first: 'Ruth', last: 'Bergman', email: 'ruth.bergman@keystone-legal.us', org: 'Keystone Legal', title: 'Counsel', city: 'Austin', country: 'United States', phone: '+1 512 555 0129' },
  { first: 'Kwame', last: 'Boateng', email: 'kwame@tidalworks.gh', org: 'Tidalworks', title: 'Operations', city: 'Takoradi', country: 'Ghana', phone: '+233 31 255 0174' },
  { first: 'Lena', last: 'Vogel', email: 'lena.vogel@ostwind.de', org: 'Ostwind', title: 'Finance', city: 'Leipzig', country: 'Germany', phone: '+49 341 555 0136' },
  { first: 'Arjun', last: 'Menon', email: 'arjun.menon@kadal.in', org: 'Kadal', title: 'Backend Engineer', city: 'Kochi', country: 'India', phone: '+91 484 555 0159' },
  { first: 'Freya', last: 'Lund', email: 'freya.lund@saltverk.is', org: 'Saltverk', title: 'Logistics', city: 'Reykjavík', country: 'Iceland', phone: '+354 555 0184' },
  { first: 'Marco', last: 'Silva', email: 'marco.silva@verdemar.pt', org: 'Verdemar', title: 'Developer', city: 'Faro', country: 'Portugal', phone: '+351 28 955 0146' },
];

/**
 * A 48 px PNG portrait for the one seeded card that has a photo, served by the
 * mock's `contacts.photo()` as the real backend serves `PHOTO` bytes.
 */
export const MOCK_CONTACT_PHOTO_PNG =
  'iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAIAAADYYG7QAAAMTElEQVR42r3W24ryCpqA4b66RiSIiIiIiIhIURRJzM4Y9xpj' +
  'jEmMqVQqlUqFIB69NzQXMXcwRyP8J30yPb3W373gvYDn5Nv87e8iNZG6iCDSEGmKtETaIh2RrkhPpC8yEBmKjETGIhORF5FX' +
  'kTcRUUQWUUQ0EUPEFLFEliJrka3IXsQWcURcEU/EFwlFIpFYJBFJRTKRXKQQKUUqkbvIQ+Rvf5eoSdQlBImGRFOiJdGW6Eh0' +
  'JXoSfYmBxFBiJDGWmEi8SLxKvEmIErKEIqFJGBKmhCWxlFhLbCX2EraEI+FKeBK+RCgRScQSiUQqkUnkEoVEKVFJ3CUe0hMk' +
  'U5OpywgyDZmmTEumLdOR6cr0ZPoyA5mhzEhmLDOReZF5lXmTEWVkGUVGkzFkTBlLZimzltnK7GVsGUfGlfFkfJlQJpKJZRKZ' +
  'VCaTyWUKmVKmkrnLPOQnaEptSn2KMKUxpTmlNaU9pTOlO6U3pT9lMGU4ZTRlPGUy5WXK65S3KeIUeYoyRZtiTDGnWFOWU9ZT' +
  'tlP2U+wpzhR3ijfFnxJOiabEU5Ip6ZRsSj6lmFJOqabcpzymT5BCTaGuICg0FJoKLYW2Qkehq9BT6CsMFIYKI4WxwkThReFV' +
  '4U1BVJAVFAVNwVAwFSyFpcJaYauwV7AVHAVXwVPwFUKFSCFWSBRShUwhVygUSoVK4a7wUJ4glZpKXUVQaag0VVoqbZWOSlel' +
  'p9JXGagMVUYqY5WJyovKq8qbiqgiqygqmoqhYqpYKkuVtcpWZa9iqzgqroqn4quEKpFKrJKopCqZSq5SqJQqlcpd5aE+QRo1' +
  'jbqGoNHQaGq0NNoaHY2uRk+jrzHQGGqMNMYaE40XjVeNNw1RQ9ZQNDQNQ8PUsDSWGmuNrcZew9ZwNFwNT8PXCDUijVgj0Ug1' +
  'Mo1co9AoNSqNu8ZDe4J0ajp1HUGnodPUaem0dTo6XZ2eTl9noDPUGemMdSY6LzqvOm86oo6so+hoOoaOqWPpLHXWOludvY6t' +
  '4+i4Op6OrxPqRDqxTqKT6mQ6uU6hU+pUOnedh/4EGdQM6gaCQcOgadAyaBt0DLoGPYO+wcBgaDAyGBtMDF4MXg3eDEQD2UAx' +
  '0AwMA9PAMlgarA22BnsD28AxcA08A98gNIgMYoPEIDXIDHKDwqA0qAzuBg/jCZpRm1GfIcxozGjOaM1oz+jM6M7ozejPGMwY' +
  'zhjNGM+YzHiZ8TrjbYY4Q56hzNBmGDPMGdaM5Yz1jO2M/Qx7hjPDneHN8GeEM6IZ8YxkRjojm5HPKGaUM6oZ9xmP2RNkUjOp' +
  'mwgmDZOmScukbdIx6Zr0TPomA5OhychkbDIxeTF5NXkzEU1kE8VEMzFMTBPLZGmyNtma7E1sE8fENfFMfJPQJDKJTRKT1CQz' +
  'yU0Kk9KkMrmbPMwnaE5tTn2OMKcxpzmnNac9pzOnO6c3pz9nMGc4ZzRnPGcy52XO65y3Of/z3//1f7Wds59jz3HmuHO8Of6c' +
  'cE40J56TzEnnZHPyOcWcck415z7nMX+CLGoWdQvBomHRtGhZtC06Fl2LnkXfYmAxtBhZjC0mFi8Wr9Y/0/xqb2FbOBauhWfh' +
  'W4QWkUVskVikFplFblFYlBaVxd3iYT1BC2oL6guEBY0FzQWtBe0FnQXdBb0F/QWDBcMFowXjBZPF/0/5x5wF7gJvgb8gXBAt' +
  'iBckC9IF2YJ8QbGgXFAtuC94LJ6gJbUl9SXCksaS5pLWkvaSzpLukt6S/pLBkuGS0ZLxksnyD4KWuEu8Jf6ScEm0JF6SLEmX' +
  'ZEvyJcWSckm15L7ksXyCVtRW1FcIKxormitaK9orOiu6K3or+isGK4YrRivGqz+m+ZW7wlvhrwhXRCviFcmKdEW2Il9RrChX' +
  'VCvuKx6rJ2hNbU19jbCmsaa5prWmvaazprumt6a/ZrBmuGa0/jOaX3lr/DXhmmhNvCZZk67J1uRrijXlmmrNfc1j/QRtqG2o' +
  'bxA2NDY0N7Q2tDd0NnQ39Db0Nww2DDeMNr8B2uBvCDdEG+INyYZ0Q7Yh31BsKDdUG+4bHpsnaEttS32LsKWxpbmltaW9pbOl' +
  'u6W3pb9lsGW4ZbT9DdAWf0u4JdoSb0m2pFuyLfmWYku5pdpy3/LYPkE7ajvqO4QdjR3NHa0d7R2dHd0dvR39HYMdwx2j3W+A' +
  'dvg7wh3RjnhHsiPdke3IdxQ7yh3VjvuOx+4J2lPbU98j7Gnsae5p7Wnv6ezp7unt6e8Z7BnuGe1/A7TH3xPuifbEe5I96Z5s' +
  'T76n2FPuqfbc9zz2T9CB2oH6AeFA40DzQOtA+0DnQPdA70D/wODA8MDo8BugA/6B8EB0ID6QHEgPZAfyA8WB8kB14H7gcXiC' +
  'bGo2dRvBpmHTtGnZtG06Nl2bnk3fZmAztBnZvwGy8W1Cm8gmtklsUpvMJrcpbEqbyuZu87CfoCO1I/UjwpHGkeaR1pH2kc6R' +
  '7pHekf6RwZHhkdHxN0BH/CPhkehIfCQ5kh7JjuRHiiPlkerI/cjj+AQ51BzqDoJDw6Hp0HJoO3Qcug49h77DwGHoMHJ+A+Tg' +
  'O4QOkUPskDikDplD7lA4lA6Vw93h4TxBJ2on6ieEE40TzROtE+0TnRPdE70T/RODE8MToxPj05/a1Ce8E/6J8ER0Ij6RnEhP' +
  'ZCfyE8WJ8kR14n7icXqCXGoudRfBpeHSdGm5tF06Ll2XnkvfZeAydBm5jF0m7h+8ZS6ui+fiu4QukUvskrikLplL7lK4lC6V' +
  'y93l4T5BZ2pn6meEM40zzTOtM+0znTPdM70z/TODM8MzozPjM5PzHwSdcc94Z/wz4ZnoTHwmOZOeyc7kZ4oz5ZnqzP3M4/wE' +
  'edQ86h6CR8Oj6dHyaHt0PLoePY++x8Bj6DHyGHtMPF48Xr1/4R/ysD0cD9fD8/A9Qo/II/ZIPFKPzCP3KDxKj8rj7vHwnqAL' +
  'tQv1C8KFxoXmhdaF9oXOhe6F3oX+hcGF4YXRhfGFyYWXC68X3i7/9GO8sL9gX3AuuBe8C/6F8EJ0Ib6QXEgvZBfyC8WF8kJ1' +
  '4X7hcXmCfGo+dR/Bp+HT9Gn5tH06Pl2fnk/fZ+Az9Bn5jH0mPi8+rz5vPqKP7KP4aD6Gj+lj+Sx91j5bn72P7eP4uD6ej+8T' +
  '+kQ+sU/ik/pkPrlP4VP6VD53n4f/BAXUAuoBQkAjoBnQCmgHdAK6Ab2AfsAgYBgwChgHTAJeAl4D3gLEADlACdACjAAzwApY' +
  'BqwDtgH7ADvACXADvAA/IAyIAuKAJCANyALygCKgDKgC7gGP4AkKqYXUQ4SQRkgzpBXSDumEdEN6If2QQcgwZBQyDpmEvIS8' +
  'hryFiCFyiBKihRghZogVsgxZh2xD9iF2iBPihnghfkgYEoXEIUlIGpKF5CFFSBlShdxDHuETdKV2pX5FuNK40rzSutK+0rnS' +
  'vdK70r8yuDK8MroyvjK58nL9VwfNvuJcca94V/wr4ZXoSnwluZJeya7kV4or5ZXqyv3K4/oERdQi6hFCRCOiGdGKaEd0IroR' +
  'vYh+xCBiGDGKGEd/ajFGeBF+RBgRRcQRSUQakUXkEUVEGVFF3CMe0RN0o3ajfkO40bjRvNG60b7RudG90bvRvzG4Mbz9+bvx' +
  'K/9GeCO6Ed9IbqQ3shv5jeJGeaO6cb/xuD1BMbWYeowQ04hpxrRi2jGdmG5ML6YfM4h/V/OrMCaKiWOSmDQmi8ljipgypoq5' +
  'xzziJ+id2jv1d4R3Gu8032m9036n8073nd77v4fyj8XvJO+k72Tv5O8U75TvVO/c33m8P0EJtYR6gpDQSGgmtBLaCZ2EbvLv' +
  '1/wqSUgTsoQ8oUgoE6qEe8IjeYI+qH1Q/0D4oPFB84PWB+0POh90P/5joA/SD7IP8g+KD8oPqg/uHzw+nqCUWko9RUhppDRT' +
  'WintlE76n9L8Kk3JUvKUIqVMqVLuKY/0Cfqk9kn9E+GTxifNT1qftD//s5pfZZ/knxSflJ9Un9w/eXw+QRm1jHqGkNHIaGa0' +
  'MtrZXwLKyDOKjDKjyrhnPLIn6IvaF/UvhC8aXzS/aH39FZpf5V8UX5RfVF/cv3h8PUE5tZx6jpDTyGnmtPK/EJRT5JQ5Vc49' +
  '55E/Qd/Uvql/I3zT+Kb5Tev7LwR9U3xTflN9c//m8f0EFdQK6gVCQaOgWdAq/kJQQVFQFlQF94JH8QT9UPuh/oPwQ+OH5g+t' +
  'n78Q9EPxQ/lD9cP9h8cP/wsqHND0DgqpOgAAAABJRU5ErkJggg==';

function buildContacts(books: AddressBook[]): Contact[] {
  const out: Contact[] = [];
  CONTACT_TABLE.forEach((row, index) => {
    // MOCK: round-robin across the three books, weighted toward Personal.
    const bookId =
      index % 5 === 4 ? 'abook-a2-clients' : index % 3 === 2 ? 'abook-a1-directory' : 'abook-a1-personal';
    const id = `contact-${pad(index + 1, 3)}`;
    const rnd = mulberry32(hashString(id));
    out.push({
      id,
      addressBookId: bookId,
      displayName: `${row.first} ${row.last}`,
      categories: index % 3 === 0 ? ['Work'] : [],
      firstName: row.first,
      lastName: row.last,
      organization: row.org,
      title: row.title,
      emails: [
        { type: 'work', value: row.email, primary: true },
        ...(index % 4 === 0
          ? [{ type: 'home', value: `${row.first.toLowerCase()}@personal-demo.net`, primary: false }]
          : []),
      ],
      phones: [
        { type: 'work', value: row.phone, primary: true },
        ...(index % 3 === 0 ? [{ type: 'mobile', value: `${row.phone} 9`, primary: false }] : []),
      ],
      addresses: [
        {
          type: 'work',
          street: `${10 + (index % 80)} ${row.last} Street`,
          city: row.city,
          region: '',
          postalCode: String(10_000 + index * 137),
          country: row.country,
        },
      ],
      ...(index % 6 === 1 ? { notes: 'Prefers email over calls. Timezone-sensitive scheduling.' } : {}),
      // One card carries every field beyond the basic form, and a photo, so
      // an edit that drops any of them is visible in mock mode (#343).
      ...(index === 0
        ? {
            nickname: 'Pri',
            urls: [{ type: 'work', value: 'https://acme-industrial.example/people/priya' }],
            birthday: '1988-04-12',
            anniversary: '2015-09-19',
            photoUrl: `/api/v1/accounts/${PRIMARY_ACCOUNT_ID}/contacts/${id}/photo`,
          }
        : {}),
      etag: `"etag-${pad(Math.floor(rnd() * 100000), 5)}"`,
      updatedAt: iso(NOW.getTime() - (index + 1) * 11 * HOUR),
    });
  });

  // Group cards (#123): read-only filters, never rows. "Family" names three
  // people of the Personal book and one who is not in it; "Book club" names
  // only people who are not. MOCK: members are `urn:uuid:<contact id>`.
  const personal = out.filter((c) => c.addressBookId === 'abook-a1-personal').slice(0, 3);
  const group = (id: string, name: string, members: string[]): Contact => ({
    id,
    addressBookId: 'abook-a1-personal',
    displayName: name,
    categories: [],
    emails: [],
    phones: [],
    addresses: [],
    isGroup: true,
    members,
    etag: `"etag-${id}"`,
    updatedAt: iso(NOW.getTime() - 3 * HOUR),
  });
  out.push(
    group('contact-group-family', 'Family', [
      ...personal.map((c) => `urn:uuid:${c.id}`),
      'urn:uuid:someone-elsewhere',
    ]),
    group('contact-group-bookclub', 'Book club', ['urn:uuid:not-here-1', 'urn:uuid:not-here-2']),
  );

  for (const book of books) {
    book.contactCount = out.filter((c) => c.addressBookId === book.id).length;
  }
  return out;
}

/* ---------------------------------------------------------------- calendar */

/**
 * Two links on one calendar, with two different modes.
 *
 * Deliberately not one: the whole point of the feature is that "Alex may see
 * my busy times" and "the team may see full details" are separate grants, and
 * a fixture with a single link would let a bug that conflates them pass. One
 * has been fetched, one never has, so the "Never used" branch has a subject.
 */
function buildCalendarShareLinks(): MockCalendarShareLink[] {
  return [
    {
      id: 'shr-a1-personal-1',
      calendarId: 'cal-a1-personal',
      label: 'Alex — work',
      privacy: 'busy-only',
      createdAt: '2026-09-01T09:00:00.000Z',
      lastUsedAt: '2026-09-19T07:31:00.000Z',
    },
    {
      id: 'shr-a1-personal-2',
      calendarId: 'cal-a1-personal',
      label: 'Family fridge tablet',
      privacy: 'full',
      createdAt: '2026-09-05T18:12:00.000Z',
    },
  ];
}

function buildCalendars(): Calendar[] {
  return [
    {
      id: 'cal-a1-personal',
      accountId: PRIMARY_ACCOUNT_ID,
      name: 'Personal',
      color: '#4f7cff',
      readOnly: false,
      href: '/dav/calendars/alex@dao-mail.com/personal/',
      visible: true,
      // Shared, with no URL: the server stores only sha256(token) per link, so
      // the listing can say THAT links exist, and how many, and nothing more.
      feedPublished: true,
      shareLinkCount: 2,
    },
    {
      id: 'cal-a1-work',
      accountId: PRIMARY_ACCOUNT_ID,
      name: 'Work',
      color: '#17a67c',
      readOnly: false,
      href: '/dav/calendars/alex@dao-mail.com/work/',
      visible: true,
    },
    {
      id: 'cal-a1-holidays',
      accountId: PRIMARY_ACCOUNT_ID,
      name: 'Public holidays',
      color: '#d9822b',
      readOnly: true,
      href: '/dav/calendars/alex@dao-mail.com/holidays/',
      visible: false,
    },
    {
      id: 'cal-a2-studio',
      accountId: SECONDARY_ACCOUNT_ID,
      name: 'Studio Nine',
      color: '#a855f7',
      readOnly: false,
      href: '/dav/calendars/alex@studio-nine.org/studio/',
      visible: true,
    },
  ];
}

const EVENT_TABLE: readonly {
  title: string;
  location: string;
  hour: number;
  durationMin: number;
  calendarId: string;
}[] = [
  { title: 'Standup', location: 'Jitsi', hour: 9, durationMin: 15, calendarId: 'cal-a1-work' },
  { title: 'Acme migration sync', location: 'Room Arvo', hour: 11, durationMin: 60, calendarId: 'cal-a1-work' },
  { title: 'Design critique', location: 'Studio Nine, floor 3', hour: 14, durationMin: 90, calendarId: 'cal-a2-studio' },
  { title: 'Northwind API review', location: 'https://meet.dao-mail.com/northwind', hour: 15, durationMin: 45, calendarId: 'cal-a1-work' },
  { title: 'Dentist', location: 'Praxis Weber', hour: 8, durationMin: 45, calendarId: 'cal-a1-personal' },
  { title: 'Invoice run', location: '', hour: 16, durationMin: 30, calendarId: 'cal-a1-personal' },
  { title: 'Coffee with Maya', location: 'Café Nord', hour: 10, durationMin: 60, calendarId: 'cal-a1-personal' },
  { title: 'Quarterly roadmap review', location: 'Room Arvo / Jitsi', hour: 13, durationMin: 120, calendarId: 'cal-a1-work' },
  { title: 'Storage capacity planning', location: 'Room Bravo', hour: 10, durationMin: 60, calendarId: 'cal-a1-work' },
  { title: 'Print proof pickup', location: 'Okafor Design', hour: 17, durationMin: 30, calendarId: 'cal-a2-studio' },
  { title: 'Sieve rollout dry run', location: 'Jitsi', hour: 12, durationMin: 45, calendarId: 'cal-a1-work' },
  { title: 'Language class', location: 'Volkshochschule', hour: 19, durationMin: 90, calendarId: 'cal-a1-personal' },
];

function attendeesFor(seed: string, size: number): Attendee[] {
  const rnd = mulberry32(hashString(seed));
  const statuses: Attendee['status'][] = ['accepted', 'declined', 'tentative', 'needs-action'];
  const out: Attendee[] = [{ ...PRIMARY_ADDRESS, status: 'accepted' }];
  for (let i = 0; i < size; i += 1) {
    const row = pick(CONTACT_TABLE, hashString(seed) + i * 3);
    out.push({
      name: `${row.first} ${row.last}`,
      email: row.email,
      status: pick(statuses, Math.floor(rnd() * 4)),
      ...(i === size - 1 ? { optional: true } : {}),
    });
  }
  return out;
}

function buildEvents(): CalendarEvent[] {
  const monthStart = startOfMonth(NOW);
  const out: CalendarEvent[] = [];
  let counter = 0;

  const push = (event: CalendarEvent): void => {
    out.push(event);
  };

  // MOCK: ~36 timed events spread across the current month.
  for (let day = 0; day < 28; day += 1) {
    const perDay = day % 3 === 0 ? 2 : day % 4 === 1 ? 1 : day % 5 === 2 ? 2 : 1;
    for (let slot = 0; slot < perDay; slot += 1) {
      if (out.length >= 36) break;
      const entry = pick(EVENT_TABLE, day * 2 + slot);
      counter += 1;
      const id = `evt-${pad(counter, 3)}`;
      const start = atTime(monthStart, day, entry.hour + slot, 0);
      const end = new Date(start.getTime() + entry.durationMin * MINUTE);
      const attendeeCount = counter % 4 === 0 ? 4 : counter % 3 === 0 ? 2 : 0;
      push({
        id,
        calendarId: entry.calendarId,
        uid: `${id}@dao-mail.com`,
        title: entry.title,
        ...(counter % 5 === 0
          ? { description: 'Agenda in the shared drive; bring the latest numbers.' }
          : {}),
        ...(entry.location !== '' ? { location: entry.location } : {}),
        start: start.toISOString(),
        end: end.toISOString(),
        allDay: false,
        status: counter % 11 === 0 ? 'tentative' : counter % 17 === 0 ? 'cancelled' : 'confirmed',
        organizer: PRIMARY_ADDRESS,
        attendees: attendeeCount > 0 ? attendeesFor(id, attendeeCount) : [],
        reminders:
          counter % 2 === 0
            ? [{ minutesBefore: 10, method: 'display' }]
            : [
                { minutesBefore: 30, method: 'push' },
                { minutesBefore: 1440, method: 'email' },
              ],
        etag: `"evt-etag-${pad(counter, 3)}"`,
      });
    }
  }

  // MOCK: recurring series — weekly standup, anchored to the 1st of the month.
  push({
    id: 'evt-rec-standup',
    calendarId: 'cal-a1-work',
    uid: 'evt-rec-standup@dao-mail.com',
    title: 'Weekly engineering standup',
    description: 'Recurring; skipped on public holidays.',
    location: 'Jitsi',
    start: atTime(monthStart, 0, 9, 30).toISOString(),
    end: atTime(monthStart, 0, 10, 0).toISOString(),
    allDay: false,
    status: 'confirmed',
    recurrenceRule: 'FREQ=WEEKLY;BYDAY=MO;COUNT=8',
    organizer: PRIMARY_ADDRESS,
    attendees: attendeesFor('evt-rec-standup', 5),
    reminders: [{ minutesBefore: 5, method: 'display' }],
    etag: '"evt-etag-rec-1"',
  });

  push({
    id: 'evt-rec-retro',
    calendarId: 'cal-a2-studio',
    uid: 'evt-rec-retro@dao-mail.com',
    title: 'Studio retro',
    location: 'Studio Nine, floor 3',
    start: atTime(monthStart, 4, 16, 0).toISOString(),
    end: atTime(monthStart, 4, 17, 0).toISOString(),
    allDay: false,
    status: 'confirmed',
    recurrenceRule: 'FREQ=WEEKLY;INTERVAL=2;BYDAY=FR',
    organizer: SECONDARY_ADDRESS,
    attendees: attendeesFor('evt-rec-retro', 3),
    reminders: [{ minutesBefore: 15, method: 'push' }],
    etag: '"evt-etag-rec-2"',
  });

  push({
    id: 'evt-rec-standup-override',
    calendarId: 'cal-a1-work',
    uid: 'evt-rec-standup@dao-mail.com',
    title: 'Weekly engineering standup (moved)',
    location: 'Room Arvo',
    start: atTime(monthStart, 14, 11, 0).toISOString(),
    end: atTime(monthStart, 14, 11, 30).toISOString(),
    allDay: false,
    status: 'confirmed',
    recurrenceId: atTime(monthStart, 14, 9, 30).toISOString(),
    organizer: PRIMARY_ADDRESS,
    attendees: attendeesFor('evt-rec-standup', 5),
    reminders: [],
    etag: '"evt-etag-rec-3"',
  });

  // MOCK: all-day entries.
  const allDays: readonly { title: string; day: number; span: number; calendarId: string }[] = [
    { title: 'Company offsite', day: 9, span: 3, calendarId: 'cal-a1-work' },
    { title: 'Public holiday', day: 17, span: 1, calendarId: 'cal-a1-holidays' },
    { title: 'Vacation', day: 22, span: 5, calendarId: 'cal-a1-personal' },
  ];
  allDays.forEach((entry, index) => {
    const id = `evt-allday-${index + 1}`;
    push({
      id,
      calendarId: entry.calendarId,
      uid: `${id}@dao-mail.com`,
      title: entry.title,
      start: atTime(monthStart, entry.day, 0, 0).toISOString(),
      end: atTime(monthStart, entry.day + entry.span, 0, 0).toISOString(),
      allDay: true,
      status: 'confirmed',
      organizer: PRIMARY_ADDRESS,
      attendees: index === 0 ? attendeesFor(id, 6) : [],
      reminders: [],
      etag: `"evt-etag-ad-${index + 1}"`,
    });
  });

  return out;
}

/* ------------------------------------------------------------------- sieve */

/**
 * MOCK: one Sieve state per mailbox (doc 05 §11.1) — there is no script list.
 *
 * The legacy account deliberately has none, and the secondary's is `foreign`
 * with `rules: null`, so the raw-editor-only path in Settings → Filters is
 * reachable without a backend.
 */
function buildSieveStates(rules: Record<string, SieveRule[]>): Record<string, SieveState> {
  return {
    [PRIMARY_ACCOUNT_ID]: {
      source: 'managed',
      rules: rules[PRIMARY_ACCOUNT_ID] ?? [],
      script: {
        name: 'ols-managed',
        active: true,
        script: [
          'require ["fileinto", "imap4flags", "vacation"];',
          '',
          '# MOCK: compiled from the structured rule set.',
          'if address :contains "from" "northwind-labs.se" {',
          '  fileinto "INBOX/Projects/Northwind";',
          '  stop;',
          '}',
          '',
          'if header :contains "subject" ["invoice", "receipt"] {',
          '  fileinto "Receipts";',
          '}',
          '',
          'if header :contains "list-id" "terminal-weekly" {',
          '  fileinto "INBOX/Newsletters";',
          '  setflag "\\\\Seen";',
          '}',
        ].join('\n'),
        updatedAt: iso(NOW.getTime() - 6 * DAY),
      },
    },
    [SECONDARY_ACCOUNT_ID]: {
      source: 'foreign',
      // Written by another client: the builder cannot own it, so the visual
      // tab must refuse rather than silently overwrite it (G6).
      rules: null,
      reason: 'This script was written by another mail client, so the rule builder cannot edit it.',
      script: {
        name: 'roundcube',
        active: true,
        script: [
          'require ["fileinto"];',
          '',
          'if address :contains "from" "okafor-design.studio" {',
          '  fileinto "INBOX/Clients";',
          '}',
        ].join('\n'),
        updatedAt: iso(NOW.getTime() - 12 * DAY),
      },
    },
    [LEGACY_ACCOUNT_ID]: { source: 'managed', rules: [], script: null },
  };
}

function buildSieveRules(): Record<string, SieveRule[]> {
  return {
    [PRIMARY_ACCOUNT_ID]: [
      {
        id: 'rule-a1-1',
        name: 'Northwind to project folder',
        enabled: true,
        match: 'any',
        conditions: [{ field: 'from', op: 'contains', value: 'northwind-labs.se' }],
        actions: [{ type: 'fileinto', value: 'INBOX/Projects/Northwind' }],
        stopProcessing: true,
        order: 1,
      },
      {
        id: 'rule-a1-2',
        name: 'Receipts and invoices',
        enabled: true,
        match: 'any',
        conditions: [
          { field: 'subject', op: 'contains', value: 'invoice' },
          { field: 'subject', op: 'contains', value: 'receipt' },
        ],
        actions: [{ type: 'fileinto', value: 'Receipts' }],
        stopProcessing: false,
        order: 2,
      },
      {
        id: 'rule-a1-3',
        name: 'Newsletters, pre-read',
        enabled: true,
        match: 'all',
        conditions: [
          { field: 'header', headerName: 'List-Id', op: 'contains', value: 'terminal-weekly' },
        ],
        actions: [
          { type: 'fileinto', value: 'INBOX/Newsletters' },
          { type: 'setflag', value: '\\Seen' },
        ],
        stopProcessing: true,
        order: 3,
      },
      {
        id: 'rule-a1-4',
        name: 'Drop oversized recruiter mail',
        enabled: false,
        match: 'all',
        conditions: [
          { field: 'from', op: 'contains', value: 'harborline-search.com' },
          { field: 'size', op: 'greater-than', value: '2M' },
        ],
        actions: [{ type: 'discard' }],
        stopProcessing: true,
        order: 4,
      },
    ],
    [SECONDARY_ACCOUNT_ID]: [
      {
        id: 'rule-a2-1',
        name: 'Client mail',
        enabled: true,
        match: 'any',
        conditions: [{ field: 'from', op: 'contains', value: 'okafor-design.studio' }],
        actions: [{ type: 'fileinto', value: 'INBOX/Clients' }],
        stopProcessing: false,
        order: 1,
      },
    ],
  };
}

/* -------------------------------------------------------------------- push */

function buildPushSubscriptions(): PushSubscriptionRecord[] {
  return [
    {
      id: 'push-sub-001',
      endpoint: 'https://push.example-service.net/v1/mock-endpoint-thinkpad',
      deviceLabel: 'Firefox on ThinkPad',
      createdAt: iso(NOW.getTime() - 21 * DAY),
      lastUsedAt: iso(NOW.getTime() - 3 * HOUR),
    },
    {
      id: 'push-sub-002',
      endpoint: 'https://push.example-service.net/v1/mock-endpoint-pixel',
      deviceLabel: 'Chrome on Pixel 8',
      createdAt: iso(NOW.getTime() - 70 * DAY),
      lastUsedAt: iso(NOW.getTime() - 2 * DAY),
    },
  ];
}

function buildNotificationPreferences(): NotificationPreferences[] {
  return [
    {
      accountId: PRIMARY_ACCOUNT_ID,
      enabled: true,
      folderIds: [folderId('a1', 'inbox'), folderId('a1', 'inbox-projects-acme')],
      calendarReminders: true,
      taskReminders: true,
      quietHours: { start: '22:30', end: '07:00', tz: 'Europe/Stockholm' },
      pushPayloadDetail: 'minimal',
    },
    {
      accountId: SECONDARY_ACCOUNT_ID,
      enabled: false,
      folderIds: [],
      calendarReminders: false,
      taskReminders: false,
      pushPayloadDetail: 'minimal',
    },
    {
      accountId: LEGACY_ACCOUNT_ID,
      enabled: false,
      folderIds: [],
      calendarReminders: false,
      taskReminders: false,
      pushPayloadDetail: 'full',
      snoozedUntil: iso(NOW.getTime() + 2 * DAY),
    },
  ];
}

/* ------------------------------------------------------------------ counts */

/** Recomputes folder unread/total from the message table. Used by mutations too. */
export function recountFolders(folders: Folder[], messages: MessageSummary[]): void {
  const totals = new Map<string, { total: number; unread: number }>();
  for (const message of messages) {
    const bucket = totals.get(message.folderId) ?? { total: 0, unread: 0 };
    bucket.total += 1;
    if (!message.flags.includes('seen')) bucket.unread += 1;
    totals.set(message.folderId, bucket);
  }
  for (const folder of folders) {
    const bucket = totals.get(folder.id);
    folder.totalCount = bucket?.total ?? 0;
    folder.unreadCount = bucket?.unread ?? 0;
  }
}

/* -------------------------------------------------------------------- seed */

/**
 * A share link as the mock stores it: the wire shape plus the calendar it
 * hangs off, because the wire shape carries no `calendar_id` (the path does).
 *
 * MOCK: the token is **not** here, in either hashed or plain form. The mock
 * models what the server can answer with, and the server cannot answer with a
 * URL after the one response that minted it.
 */
export interface MockCalendarShareLink extends CalendarShareLink {
  calendarId: string;
}

export interface MockSeed {
  session: Session;
  accounts: Account[];
  folders: Folder[];
  messages: MessageSummary[];
  drafts: Draft[];
  addressBooks: AddressBook[];
  contacts: Contact[];
  calendars: Calendar[];
  /** Named share links, keyed to a calendar. Oldest first, as the API lists them. */
  calendarShareLinks: MockCalendarShareLink[];
  events: CalendarEvent[];
  /** One Sieve state per account id (doc 10 §6.5), not a list of scripts. */
  sieve: Record<string, SieveState>;
  pushSubscriptions: PushSubscriptionRecord[];
  notificationPreferences: NotificationPreferences[];
}

const MESSAGE_SPECS: readonly MessageSpec[] = [
  // MOCK: 234 rows in the primary Inbox so the virtualized list is exercised.
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'inbox', count: 234, spanDays: 60 },
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'inbox-projects', count: 18, spanDays: 45 },
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'inbox-projects-acme', count: 26, spanDays: 50 },
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'inbox-projects-northwind', count: 14, spanDays: 40 },
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'inbox-newsletters', count: 31, spanDays: 55 },
  {
    accountId: PRIMARY_ACCOUNT_ID,
    folderSlug: 'sent',
    count: 42,
    spanDays: 60,
    outgoing: true,
    allRead: true,
  },
  {
    accountId: PRIMARY_ACCOUNT_ID,
    folderSlug: 'drafts',
    count: 3,
    spanDays: 10,
    outgoing: true,
    draft: true,
    allRead: true,
  },
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'archive', count: 57, spanDays: 180, allRead: true },
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'junk', count: 12, spanDays: 20 },
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'trash', count: 9, spanDays: 14, allRead: true },
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'receipts', count: 23, spanDays: 120, allRead: true },
  { accountId: PRIMARY_ACCOUNT_ID, folderSlug: 'travel', count: 7, spanDays: 90, allRead: true },

  { accountId: SECONDARY_ACCOUNT_ID, folderSlug: 'inbox', count: 48, spanDays: 45, owner: SECONDARY_ADDRESS },
  {
    accountId: SECONDARY_ACCOUNT_ID,
    folderSlug: 'inbox-clients',
    count: 16,
    spanDays: 40,
    owner: SECONDARY_ADDRESS,
  },
  {
    accountId: SECONDARY_ACCOUNT_ID,
    folderSlug: 'sent',
    count: 19,
    spanDays: 45,
    owner: SECONDARY_ADDRESS,
    outgoing: true,
    allRead: true,
  },
  {
    accountId: SECONDARY_ACCOUNT_ID,
    folderSlug: 'archive',
    count: 22,
    spanDays: 150,
    owner: SECONDARY_ADDRESS,
    allRead: true,
  },
  {
    accountId: SECONDARY_ACCOUNT_ID,
    folderSlug: 'invoices',
    count: 14,
    spanDays: 200,
    owner: SECONDARY_ADDRESS,
    allRead: true,
  },
  { accountId: SECONDARY_ACCOUNT_ID, folderSlug: 'junk', count: 5, spanDays: 15, owner: SECONDARY_ADDRESS },

  { accountId: LEGACY_ACCOUNT_ID, folderSlug: 'inbox', count: 11, spanDays: 30, owner: LEGACY_ADDRESS },
  {
    accountId: LEGACY_ACCOUNT_ID,
    folderSlug: 'sent',
    count: 6,
    spanDays: 60,
    owner: LEGACY_ADDRESS,
    outgoing: true,
    allRead: true,
  },
];

/**
 * MOCK: builds a fresh, fully independent object graph every call, so the mock
 * adapter can "clone" the fixtures simply by asking for a new seed.
 */
export function createSeedData(): MockSeed {
  const accounts = buildAccounts();
  const folders: Folder[] = [
    ...buildFolders(PRIMARY_ACCOUNT_ID, PRIMARY_FOLDER_SPECS),
    ...buildFolders(SECONDARY_ACCOUNT_ID, SECONDARY_FOLDER_SPECS),
    ...buildFolders(LEGACY_ACCOUNT_ID, LEGACY_FOLDER_SPECS),
  ];

  const messages: MessageSummary[] = [];
  for (const spec of MESSAGE_SPECS) messages.push(...buildMessages(spec));
  recountFolders(folders, messages);

  const addressBooks = buildAddressBooks();
  const contacts = buildContacts(addressBooks);

  const session: Session = {
    // No token: cookie sessions leave nothing for JS to hold (ADR 0001).
    expiresAt: iso(NOW.getTime() + 12 * HOUR),
    absoluteExpiresAt: iso(NOW.getTime() + 24 * HOUR),
    // No `id` of its own and no display name: the owner *is* the email
    // (ADR 0010). `account.id` is the primary mailbox's derived account id.
    account: {
      id: PRIMARY_ACCOUNT_ID,
      email: PRIMARY_ADDRESS.email,
      domain: PRIMARY_ADDRESS.email.split('@')[1] ?? '',
      authHost: PRIMARY_AUTH_HOST,
    },
  };

  return {
    session,
    accounts,
    folders,
    messages,
    drafts: buildDrafts(),
    addressBooks,
    contacts,
    calendars: buildCalendars(),
    calendarShareLinks: buildCalendarShareLinks(),
    events: buildEvents(),
    sieve: buildSieveStates(buildSieveRules()),
    pushSubscriptions: buildPushSubscriptions(),
    notificationPreferences: buildNotificationPreferences(),
  };
}
